import { useEffect, useState, useMemo } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import type { ThunkDispatch } from "redux-thunk";

import AccountDashboard from "../components/general_user_manage/AccountDashboard";
import BulkUserCreateModal, {
  type BulkCreateProgress,
} from "../components/general_user_manage/BulkUserCreateModal";
import CreditWarningModal from "../components/general_user_manage/CreditWarningModal";
import EnterpriseRequiredNoti from "../components/general_user_manage/EnterpriseRequiredNoti";
import ErrorModal from "../components/general_user_manage/ErrorModal";
import GeneralUserList from "../components/general_user_manage/GeneralUserList";
import GeneralUserManageNavBar from "../components/general_user_manage/GeneralUserManageNavBar";
import LimitSettingModal from "../components/general_user_manage/LimitSettingModal";
import LoadingIndicatorModal from "../components/general_user_manage/LoadingIndicatorModal";
import ProgressIndicatorModal from "../components/general_user_manage/ProgressIndicatorModal";
import UserDeleteModal from "../components/general_user_manage/UserDeleteModal";
import UserDetailModal from "../components/general_user_manage/UserDetailModal";
import UserInfoModal from "../components/general_user_manage/UserInfoModal";
import Toast from "../components/Toast";
import { RoleTypeExtension, PlanTypeExtension } from "../constants";
import { accountUsersActions } from "../reducers/accountUsers";
import type {
  AccountUsersData,
  GeneralUserData,
} from "../types/generalUserManage";
import { getBoolean, setBoolean } from "../utils/localStorage";

interface RootState {
  currentUser: {
    user_id?: number;
    roleType?: string;
    planType?: string;
    account?: {
      id: number;
    };
  } & Record<string, unknown>;
  accountUsers: {
    data: AccountUsersData | null;
    loading: boolean;
    error: string | null;
  };
}

function mapStateToProps(state: RootState) {
  return {
    ...state.currentUser,
    accountUsers: state.accountUsers,
  };
}

const GeneralUserManage = () => {
  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, unknown>>();
  const [searchParams] = useSearchParams();
  const accountIdParam = searchParams.get("account_id");

  // URLパラメータにaccount_idがある = アプリ管理者による閲覧
  const isStaffUser = !!accountIdParam;

  // Redux stateから取得
  const { data, loading, error } = useSelector(
    (state: RootState) => state.accountUsers,
  );
  const currentUserId = useSelector(
    (state: RootState) => state.currentUser.user_id,
  );
  const currentUserAccountId = useSelector(
    (state: RootState) => state.currentUser.account?.id,
  );
  const roleType = useSelector(
    (state: RootState) => state.currentUser.roleType,
  );
  const planType = data?.account?.plan_type?.name;
  const isViewer = RoleTypeExtension.isViewer(roleType);
  const accountData = data?.account || null;

  // accountIdを決定（URLパラメータ優先、なければログインユーザーのaccount_id）
  const accountId = accountIdParam
    ? parseInt(accountIdParam, 10)
    : currentUserAccountId;

  // 今月のaccountUsersデータを保持（モーダル表示時に使用）
  const [currentMonthUsers, setCurrentMonthUsers] = useState<GeneralUserData[]>(
    [],
  );

  // is_activeがtrueのユーザーのみフィルタリング
  const usersData = useMemo(() => {
    if (!data?.users) return [];
    return data.users.filter((user) => user.is_active);
  }, [data?.users]);

  // アクティブユーザーの割振済クレジット合計を計算
  const totalUserCreditLimit = useMemo(() => {
    return usersData.reduce((sum, user) => sum + (user.credit_limit || 0), 0);
  }, [usersData]);

  // アクティブユーザーの使用済みクレジット合計を計算
  const totalUserCreditConsumed = useMemo(() => {
    return usersData.reduce(
      (sum, user) => sum + (user.credit_consumed || 0),
      0,
    );
  }, [usersData]);

  // モーダル用のステート
  const [isShowBulkUserCreateModal, setIsShowBulkUserCreateModal] =
    useState(false);
  const [isShowUserInfoModal, setIsShowUserInfoModal] = useState(false);
  const [isShowUserDetailModal, setIsShowUserDetailModal] = useState(false);
  const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
  const [isShowLimitSettingModal, setIsShowLimitSettingModal] = useState(false);
  const [isShowCreditWarningModal, setIsShowCreditWarningModal] =
    useState(false);
  const [creditWarningStorageKey, setCreditWarningStorageKey] = useState<
    string | null
  >(null);
  const [isCreate, setIsCreate] = useState(true);
  const [selectedUser, setSelectedUser] = useState<GeneralUserData | null>(
    null,
  );
  // 一括登録の進捗状態
  const [bulkCreateProgress, setBulkCreateProgress] =
    useState<BulkCreateProgress | null>(null);

  // 表示期間の状態管理（デフォルトは現在月）
  const [displayPeriod, setDisplayPeriod] = useState<string>(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  });

  // 今月判定
  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return displayPeriod === currentMonth;
  }, [displayPeriod]);

  // 月テキスト生成
  const periodMonthText = useMemo(() => {
    if (isCurrentMonth) {
      return "今月";
    }
    // displayPeriod が "2025-10" の形式なので、月部分を取り出す
    const month = displayPeriod.split("-")[1];
    return `${parseInt(month, 10)}月`;
  }, [displayPeriod, isCurrentMonth]);

  // 初期データ取得
  useEffect(() => {
    if (accountId) {
      dispatch(accountUsersActions.fetchAccountUsers(accountId));
    }

    // コンポーネントアンマウント時にstateをクリア
    return () => {
      dispatch(accountUsersActions.clearData());
    };
  }, [dispatch, accountId]);

  // 今月のデータを保存（モーダル用）
  useEffect(() => {
    if (isCurrentMonth && usersData.length > 0) {
      setCurrentMonthUsers(usersData);
    }
  }, [isCurrentMonth, usersData]);

  // クレジット警告チェック
  useEffect(() => {
    // アカウント管理画面から遷移してきた場合は警告を表示しない
    if (isStaffUser) return;

    if (!accountData) return;

    const { id, credit_limit, credit_consumed } = accountData;
    if (!credit_limit || credit_limit === 0) return;

    // 残り10%以下かチェック
    const remainingPercentage =
      ((credit_limit - credit_consumed) / credit_limit) * 100;
    if (remainingPercentage > 10) return;

    // ストレージキーを生成 (credit_warning_dismissed_YYYYMM_accountId)
    const now = new Date();
    const yyyymm = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
    const storageKey = `credit_warning_dismissed_${yyyymm}_${id}`;

    // すでに今月表示済みかチェック
    if (!getBoolean(storageKey)) {
      setCreditWarningStorageKey(storageKey);
      setIsShowCreditWarningModal(true);
    }
  }, [accountData, isStaffUser]);

  // 表示期間変更時のハンドラ
  const handleDisplayPeriodChange = (period: string) => {
    setDisplayPeriod(period);

    if (usersData && usersData.length > 0) {
      const [year, month] = period.split("-");
      const startDate = `${year}-${month}-01`;
      // 翌月の1日を計算
      const nextMonth = new Date(parseInt(year, 10), parseInt(month, 10), 1);
      const endDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}-01`;

      const userIds = usersData.map((user) => user.user_id);
      dispatch(
        accountUsersActions.getHistoricalUsage({
          account_id: accountData.id,
          user_ids: userIds,
          start_date: startDate,
          end_date: endDate,
        }),
      );
    }
  };

  // リトライ処理
  const handleRetry = () => {
    if (accountId) {
      dispatch(accountUsersActions.fetchAccountUsers(accountId));
    }
  };

  // モーダル操作用の関数
  const handleBulkCreateUser = () => {
    setIsShowBulkUserCreateModal(true);
  };

  const handleCloseBulkUserCreateModal = () => {
    setIsShowBulkUserCreateModal(false);
  };

  const handleCreateUser = () => {
    setIsCreate(true);
    setSelectedUser(null);
    setIsShowUserInfoModal(true);
  };

  const handleEditUser = (user: GeneralUserData) => {
    setIsCreate(false);
    // 今月のデータから該当ユーザーを検索
    const currentMonthUser = currentMonthUsers.find(
      (u) => u.user_id === user.user_id,
    );
    setSelectedUser(currentMonthUser || user);
    setIsShowUserInfoModal(true);
  };

  const handleViewUser = (user: GeneralUserData) => {
    // 今月のデータから該当ユーザーを検索
    const currentMonthUser = currentMonthUsers.find(
      (u) => u.user_id === user.user_id,
    );
    setSelectedUser(currentMonthUser || user);
    setIsShowUserDetailModal(true);
  };

  const handleCloseModal = () => {
    setIsShowUserInfoModal(false);
    setSelectedUser(null);
  };

  const handleCloseDetailModal = () => {
    setIsShowUserDetailModal(false);
    setSelectedUser(null);
  };

  const handleEditFromDetail = () => {
    setIsShowUserDetailModal(false);
    setIsCreate(false);
    setIsShowUserInfoModal(true);
  };

  const handleUserError = (errorMessage: string) => {
    dispatch(accountUsersActions.setError(errorMessage));
  };

  const handleDeleteUser = (user: GeneralUserData) => {
    // 今月のデータから該当ユーザーを検索
    const currentMonthUser = currentMonthUsers.find(
      (u) => u.user_id === user.user_id,
    );
    setSelectedUser(currentMonthUser || user);
    setIsShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setIsShowDeleteModal(false);
    setSelectedUser(null);
  };

  const handleLimitSetting = () => {
    setIsShowLimitSettingModal(true);
  };

  const handleCloseLimitSettingModal = () => {
    setIsShowLimitSettingModal(false);
  };

  const handleCloseCreditWarningModal = (dontShowAgain: boolean) => {
    if (dontShowAgain && creditWarningStorageKey) {
      setBoolean(creditWarningStorageKey, true);
    }
    setIsShowCreditWarningModal(false);
    setCreditWarningStorageKey(null);
  };

  return (
    <>
      <Toast />
      {/* ナビゲーションバー（共通） */}
      <GeneralUserManageNavBar />

      {/* Enterprise必須の通知（Enterprise以外の場合のみ表示） */}
      {planType && !PlanTypeExtension.isEnterprise(planType) && (
        <EnterpriseRequiredNoti planType={planType} />
      )}

      {/* メインコンテンツ */}
      {data && (
        // データ表示（flexboxで高さを制御し、ナビゲーションバー分を引く）
        <div className="flex flex-col" style={{ height: "calc(100vh - 56px)" }}>
          {/* アカウントダッシュボード */}
          <AccountDashboard
            accountData={accountData}
            totalUserCreditLimit={totalUserCreditLimit}
            totalUserCreditConsumed={totalUserCreditConsumed}
            displayPeriod={displayPeriod}
            onDisplayPeriodChange={handleDisplayPeriodChange}
          />

          {/* ユーザーリスト（残りの高さを全て使う） */}
          <div className="flex-1 min-h-0">
            <GeneralUserList
              users={usersData}
              periodMonthText={periodMonthText}
              currentUserId={currentUserId}
              isViewer={isViewer}
              onBulkCreateUser={handleBulkCreateUser}
              onCreateUser={handleCreateUser}
              onEditUser={handleEditUser}
              onViewUser={handleViewUser}
              onDeleteUser={handleDeleteUser}
              onLimitSetting={handleLimitSetting}
            />
          </div>
        </div>
      )}

      {/* ユーザー詳細モーダル */}
      {isShowUserDetailModal && selectedUser && (
        <UserDetailModal
          onClose={handleCloseDetailModal}
          userData={selectedUser}
          onEdit={handleEditFromDetail}
          isViewer={isViewer}
        />
      )}

      {/* ユーザー情報（作成・編集）モーダル */}
      {isShowUserInfoModal && (
        <UserInfoModal
          onClose={handleCloseModal}
          isCreate={isCreate}
          userData={selectedUser || undefined}
          currentUserId={currentUserId}
          onError={handleUserError}
          accountData={accountData}
          users={data?.users || []}
        />
      )}

      {/* ユーザー削除モーダル */}
      {isShowDeleteModal && selectedUser && accountData && (
        <UserDeleteModal
          accountId={accountData.id}
          userId={selectedUser.user_id}
          employeeId={selectedUser.employee_id || 0}
          fullName={selectedUser.full_name || selectedUser.user_name}
          onClose={handleCloseDeleteModal}
        />
      )}

      {/* 上限一括設定モーダル */}
      {isShowLimitSettingModal && accountData && (
        <LimitSettingModal
          onClose={handleCloseLimitSettingModal}
          accountData={accountData}
          users={currentMonthUsers}
        />
      )}

      {/* 一括ユーザー追加モーダル */}
      {isShowBulkUserCreateModal && accountData && (
        <BulkUserCreateModal
          accountId={accountData.id}
          accountCreditLimit={accountData.credit_limit ?? null}
          existingAllocatedCredit={totalUserCreditLimit}
          onClose={handleCloseBulkUserCreateModal}
          onProgressChange={setBulkCreateProgress}
        />
      )}

      {/* 一括登録の進捗モーダル */}
      {bulkCreateProgress && (
        <ProgressIndicatorModal
          current={bulkCreateProgress.current}
          total={bulkCreateProgress.total}
        />
      )}

      {/* 読込中モーダル */}
      {loading && <LoadingIndicatorModal />}

      {/* エラーモーダル */}
      {error && (
        <ErrorModal
          onClose={() => dispatch(accountUsersActions.clearError())}
          title="エラー"
          message={error}
          onRetry={error.includes("取得") ? handleRetry : undefined}
        />
      )}

      {/* クレジット警告モーダル */}
      {isShowCreditWarningModal && accountData && (
        <CreditWarningModal
          onClose={handleCloseCreditWarningModal}
          creditLimit={accountData.credit_limit || 0}
          creditConsumed={accountData.credit_consumed || 0}
          remainingPercentage={
            accountData.credit_limit
              ? ((accountData.credit_limit -
                  (accountData.credit_consumed || 0)) /
                  accountData.credit_limit) *
                100
              : 100
          }
        />
      )}
    </>
  );
};

export default connect(mapStateToProps)(GeneralUserManage);
